import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/shared/lib/cn";
import { Tooltip } from "./Tooltip";

/**
 * Compact icon control (topbar utilities, submit, and composer tools).
 * Pass a single icon as children; sizing/centering is handled here.
 */
export const iconButtonVariants = cva(
  "studio-button relative inline-grid shrink-0 place-items-center rounded-icon-button outline-none transition-[background-color,color,transform] duration-150 ease-house focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-40",
  {
    variants: {
      tone: {
        neutral: "bg-transparent text-ink-soft hover:bg-hover",
        soft: "bg-transparent text-ink-soft hover:bg-hover",
        glass: "studio-glass-button text-ink",
        video: "studio-action bg-[var(--badge-video)] text-[var(--media-video-on)] [--button-glow:var(--media-video)]",
        outline: "studio-outline border border-line-strong bg-transparent text-ink hover:border-ink-soft hover:bg-hover",
        accent:
          "studio-action bg-action-bg text-action-text hover:bg-action-bg-hover",
        image: "studio-action bg-[var(--badge-image)] text-white [--button-glow:var(--media-image)] hover:bg-[var(--button-image-hover)]",
        voice: "studio-action bg-[var(--badge-voice)] text-[var(--media-voice-on)] [--button-glow:var(--media-voice)] hover:bg-[var(--button-voice-hover)]",
        danger:
          "rounded-sm bg-danger-subtle text-danger hover:bg-danger-solid hover:text-white focus-visible:outline-danger",
      },
      size: {
        sm: "size-11 sm:size-8 [&_svg]:size-4",
        md: "size-11 sm:size-9 [&_svg]:size-[18px]",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
);

export interface IconButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  /** Required for a11y — icon buttons have no text. */
  "aria-label": string;
  /** Optional styled tooltip; use the same action wording as the aria-label. */
  tooltip?: ReactNode;
  tooltipSide?: "top" | "bottom";
  tooltipAlign?: "center" | "start" | "end";
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      className,
      tone,
      size,
      type = "button",
      title,
      tooltip,
      tooltipSide = "bottom",
      tooltipAlign = "center",
      children,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) {
    const button = (
      <button
        ref={ref}
        type={type}
        title={tooltip === undefined ? (title ?? ariaLabel) : undefined}
        aria-label={ariaLabel}
        className={cn(iconButtonVariants({ tone, size }), className)}
        {...props}
        data-finish="satin"
        data-quiet={tone === undefined || tone === "neutral" || tone === "soft" || undefined}
      >
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center"
        >
          {children}
        </span>
      </button>
    );

    return tooltip === undefined ? (
      button
    ) : (
      <Tooltip content={tooltip} side={tooltipSide} align={tooltipAlign} describe={false}>
        {button}
      </Tooltip>
    );
  },
);
